/**
 * MobileBottomNav — 6-tab bottom navigation for mobile participant view.
 * Tabs: Actions | Confidential | Country | World | News | Navigator
 * Map is now a sub-tab of World. Newsbrief gets its own tab.
 * Navigator tab shows gold glow when nudge is active.
 */

import { List, Lock, Flag, Globe, Newspaper, Compass } from 'lucide-react'

export type MobileTab = 'actions' | 'confidential' | 'country' | 'world' | 'newsbrief' | 'navigator'

interface Props {
  activeTab: MobileTab
  onTabChange: (tab: MobileTab) => void
  hasRole: boolean
  actionsBadge?: number
  unreadBadge?: number
  navNudge?: boolean
}

const TABS: { id: MobileTab; label: string; icon: typeof List; requiresRole: boolean }[] = [
  { id: 'actions', label: 'Actions', icon: List, requiresRole: true },
  { id: 'confidential', label: 'Confid.', icon: Lock, requiresRole: true },
  { id: 'country', label: 'Country', icon: Flag, requiresRole: true },
  { id: 'world', label: 'World', icon: Globe, requiresRole: false },
  { id: 'newsbrief', label: 'News', icon: Newspaper, requiresRole: true },
  { id: 'navigator', label: 'Nav.', icon: Compass, requiresRole: false },
]

export function MobileBottomNav({ activeTab, onTabChange, hasRole, actionsBadge, unreadBadge, navNudge }: Props) {
  const visibleTabs = TABS.filter(t => !t.requiresRole || hasRole)

  return (
    <nav
      className="sticky bottom-0 z-40 bg-card border-t border-border flex"
      style={{ paddingBottom: 'calc(6px + env(safe-area-inset-bottom, 0px))' }}
    >
      {visibleTabs.map(t => {
        const isActive = activeTab === t.id
        const isNavigator = t.id === 'navigator'
        const Icon = t.icon
        const badge = t.id === 'actions' ? actionsBadge : t.id === 'confidential' ? unreadBadge : undefined

        return (
          <button
            key={t.id}
            onClick={() => onTabChange(t.id)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[48px] relative transition-colors ${
              isActive
                ? 'text-action'
                : isNavigator && navNudge
                  ? 'text-warning'
                  : 'text-text-secondary/60'
            }`}
          >
            <Icon size={18} strokeWidth={isActive ? 2.5 : 1.75} />
            <span className={`text-[9px] font-medium ${isActive ? 'font-bold' : ''}`}>
              {t.label}
            </span>

            {/* Active indicator */}
            {isActive && (
              <div className="absolute bottom-0 left-1/4 right-1/4 h-[3px] rounded-full bg-action" />
            )}

            {/* Navigator gold glow */}
            {isNavigator && navNudge && !isActive && (
              <div className="absolute inset-0 rounded-lg animate-pulse" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)' }} />
            )}

            {/* Badge */}
            {badge && badge > 0 && (
              <span className="absolute top-1 right-1/4 w-4 h-4 bg-danger text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {badge > 9 ? '9+' : badge}
              </span>
            )}
          </button>
        )
      })}
    </nav>
  )
}
